import React, { useState, useEffect } from 'react';
import { Card, Box, CardHeader, TextField, CardContent, Divider, Grid, Typography, Button, Snackbar, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AddStudyPage = () => {
  const [newStudyData, setNewStudyData] = useState({ title: '', bu_master: '', description: '', category: '', author: '', image: '' });
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [buMasters, setBuMasters] = useState([]);
  const [loading, setLoading] = useState(false);  // Added loading state
  const navigate = useNavigate();

  const getUserDetailsFromLocalStorage = () => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      return { username: decodedToken.username };
    }
    return { username: '' };
  };

  useEffect(() => {
    const { username } = getUserDetailsFromLocalStorage();
    setNewStudyData((prevData) => ({
      ...prevData,
      author: username || '',
    }));

    fetchBuMasters();
  }, []);

  const fetchBuMasters = async () => {
    try {
      const response = await axios.get('https://3wzg6m6x-5000.asse.devtunnels.ms/api/bu_master');
      setBuMasters(response.data);
    } catch (error) {
      console.error('Error fetching BU Masters:', error);
    }
  };

  const handleAddStudy = async () => {
    setLoading(true);  // Set loading to true when the form is being submitted
    const formDatas = new FormData();
    formDatas.append('author', newStudyData.author);
    formDatas.append('id_bu_master', newStudyData.bu_master);
    formDatas.append('description', newStudyData.description);
    formDatas.append('category', newStudyData.category);
    formDatas.append('title', newStudyData.title);
  
    if (imageFile) {
      formDatas.append('image', imageFile);
    } else {
      console.warn("No image file selected");
    }
  
    try {
      const token = localStorage.getItem('token');
      await axios.post('https://3wzg6m6x-5000.asse.devtunnels.ms/api/study/add', formDatas, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
  
      Swal.fire({
        icon: 'success',
        title: 'Study added successfully!',
        showConfirmButton: false,
        timer: 1500
      });
  
      navigate('/compro/study-case');
    } catch (error) {
      console.error('Database Error:', error.response?.data);
      Swal.fire({
        icon: 'error',
        title: 'Failed to add study',
        text: error.response?.data?.message || 'An error occurred',
      });
    } finally {
      setLoading(false);  // Set loading to false after the operation completes
    }
  };

  const handleCancel = () => {
    navigate('/compro/study-case');  // Redirect to the study case page
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 3, padding: 3 }}>
      <CardHeader
        title={<Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold' }}>Add Study Case</Typography>}
        sx={{ paddingBottom: 2 }}
      />
      <Divider />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Title"
              value={newStudyData.title}
              onChange={(e) => setNewStudyData({ ...newStudyData, title: e.target.value })}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2 }}>
              <InputLabel>BU Master</InputLabel>
              <Select
                value={newStudyData.bu_master}
                onChange={(e) => setNewStudyData({ ...newStudyData, bu_master: e.target.value })}
                label="BU Master"
              >
                {buMasters.map((bu) => (
                  <MenuItem key={bu.id} value={bu.id}>{bu.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <ReactQuill
              value={newStudyData.description}
              onChange={(value) => setNewStudyData({ ...newStudyData, description: value })}
              modules={{
                toolbar: [
                  [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                  ['bold', 'italic', 'underline'],
                  ['link'],
                  [{ 'align': [] }],
                  ['image'],
                ],
              }}
              style={{ height: '150px', width: '100%' }}
            />
          </Grid>

          <Grid item xs={12} md={6} style={{ marginTop: '56px' }}>
            <TextField
              fullWidth
              label="Category"
              value={newStudyData.category}
              onChange={(e) => setNewStudyData({ ...newStudyData, category: e.target.value })}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
          </Grid>

          <Grid item xs={12} md={6} style={{ marginTop: '40px' }}>
            <TextField
              label="Author"
              variant="outlined"
              fullWidth
              margin="normal"
              value={newStudyData.author}
              InputProps={{
                readOnly: true,
              }}
              sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRadius: 2 }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <input type="file" onChange={handleImageChange} style={{ display: 'none' }} id="image-upload" />
            <label htmlFor="image-upload">
              <Button variant="outlined" fullWidth component="span" sx={{ borderRadius: 2, boxShadow: 2 }}>
                Upload Image
              </Button>
            </label>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  marginTop: '16px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                }}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                sx={{ marginRight: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddStudy}
                disabled={loading}  // Disable button while loading
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AddStudyPage;
